import React from "react";
import Styles from "./card.module.css";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";

/**
 * Format availability text for display.
 * Converts availability data (string or array) into readable text describing
 * which customer tiers the perk is available for.
 * @param {string|Array} availability - Single tier name or array of tier names
 * @returns {string} Formatted availability text (e.g., "Standard and Premium only")
 */
const formatAvailability = (availability) => {
  if (!availability) return "";

  if (typeof availability === "string") return availability;

  if (Array.isArray(availability)) {
    const len = availability.length;
    if (len === 0) return "";
    if (len === 1) return availability[0];
    if (len === 2) return `${availability[0]} and ${availability[1]} only`;
    return `${availability.slice(0, -1).join(", ")} and ${availability[len - 1]} only`;
  }

  return "";
};

/**
 * PartnersPerkCard Component
 *
 * Displays a partner perk offer card with logo, name, description, industry/categories,
 * and tier badges (Freemium, Standard, Premium). Shows availability information and
 * a "View offer" button that opens the perk detail modal.
 *
 * @param {Object} props - Component props
 * @param {Object} props.perk - Perk object containing name, description, logo, industry, categories, bundleTiers, userAvailability
 * @param {Function} props.handleOpenModal - Callback to open perk detail modal
 * @returns {JSX.Element} Partner perk offer card with badge and action button
 */
const PartnersPerkCard = ({ perk, handleOpenModal }) => {
  // Helper functions to check which tier badges should display

  return (
    <div className={Styles.indexcardrow}>
      <div
        className={`${Styles.indexcard} ${Styles.indexf} ${Styles.indeximgthumbnail} ${Styles.indexfxlayout1}`}
      >
        <div className={Styles.indexcardbottom}>
          <div>
            <div className={Styles.indexlogobox}>
              {perk.logo === "" ? (
                <img
                  src="/images/logo/noimage.png"
                  className={Styles.indexlogo}
                  alt=""
                />
              ) : (
                <img
                  src={perk.logo}
                  alt={perk.name}
                  className="img-fluid d-block mx-auto"
                  style={{
                    minHeight: "50px",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
            <span className={Styles.indexcompanyname}>
              <h3>{perk.name || ""}</h3>
            </span>
            <span className={Styles.indexcompanyname2}>
              <p>{perk.description || ""}</p>
            </span>
          </div>
          <div className={Styles.indextag} style={{ textAlign: "left" }}>
            <Link to="#">
              {perk.industry && (
                <span className={Styles.indexindustrybutton}>
                  {perk.industry}
                </span>
              )}
            </Link>
            {perk.categories &&
              perk.categories.slice(0, 2).map((category, index) => (
                <Link to="#" key={index}>
                  <span className={Styles.indexcategorybutton}>{category}</span>
                </Link>
              ))}
          </div>
          <div className="d-grid" style={{ textAlign: "center" }}>
            <Button
              onClick={() => handleOpenModal(perk)}
              size="sm"
              className="glintanimation navbaraddnew"
              style={{ width: "fit-content", placeSelf: "center" }}
            >
              View offer
            </Button>
          </div>
          <div
            style={{
              textAlign: "right",
              paddingRight: "0.5rem",
              paddingBottom: "0.4rem",
              fontSize: "11px",
            }}
          >
            <span>
              Available for {formatAvailability(perk.userAvailability)}{" "}
              customers {formatAvailability(perk.userAvailabilityExtra)}{" "}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnersPerkCard;
